import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { ChatMessageItem } from '../../../graphql/API';
import { useVenueUpdateChatMessageMutation } from '../../../hooks/reactQuery/chat/useAdminUpdateChatMessageMutation';
import LoadingButton from '../../Button/LoadingButton';

type Props = {
  open: boolean;
  onClose: () => void;
  messageItem: Nullable<ChatMessageItem | undefined>;
};

export const EditMessageDialog: React.FC<Props> = ({ open, onClose, messageItem }) => {
  const messageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const isMobileScreen = useMediaQuery('(max-width:486px)');
  const theme = useTheme();

  const [newMessage, setNewMessage] = React.useState('');

  const { mutateAsync, isPending } = useVenueUpdateChatMessageMutation({
    onSuccess: () => {
      onClose();
    },
  });

  const focusInput = React.useCallback(() => {
    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, []);

  React.useEffect(() => {
    focusInput();
  }, [focusInput]);

  React.useEffect(() => {
    setNewMessage(messageItem?.message || '');
  }, [messageItem, open]);

  const onMessageSubmitHandle = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (messageItem) {
      mutateAsync({
        conversationId: messageItem.conversationId,
        message: newMessage,
        messageTime: messageItem.messageTime,
      });
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      onClose={onClose}
      aria-labelledby='message-dialog-title'
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '14px',
          backgroundColor: '#F8F8F9',
          boxShadow: '0px 0px 10px 0px rgba(24, 35, 46, 0.15)',
          p: isMobileScreen ? 1 : 2,
        },
      }}
    >
      <form onSubmit={onMessageSubmitHandle}>
        <Box sx={{ px: 2, pt: 2, pb: 0, position: 'relative' }}>
          <Typography id='message-dialog-title' variant='h6'>
            Edit Message
          </Typography>
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 1 }}>
          <StyledInput
            ref={messageInputRef}
            placeholder='Type your message'
            minRows={5}
            maxRows={7}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <LoadingButton
            loading={isPending}
            variant='contained'
            color='primary'
            type='submit'
            disabled={!newMessage || messageItem?.message === newMessage || isPending}
          >
            Save changes
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const StyledInput = styled(TextareaAutosize)(({ theme }) => ({
  backgroundColor: '#fff',
  width: '100%',
  fontSize: 15,
  outline: 'none',
  borderRadius: 12,
  border: '1px solid #e9e9e9',
  padding: '18px 15px',
  resize: 'none',
  '&:focus': {
    outline: 'none',
  },
}));
